export interface FeatureEntry {
  /** Matches the numbering in docs/V18_FEATURE_INVENTORY.md exactly. */
  id: number;
  name: string;
  category: string;
  summary: string;
  /** Set only for features with a confirmed, disclosed limitation in v18 — surfaced in the UI rather than hidden. */
  limitation?: string;
}

/**
 * Sourced directly from docs/V18_FEATURE_INVENTORY.md — every summary here
 * describes only what the bot actually does today. Limitations (verification
 * gap, economy no-faucet, always-on logs) are carried through rather than
 * smoothed over, per the project's explicit accuracy requirements.
 */
export const features: FeatureEntry[] = [
  {
    id: 1,
    name: 'Moderation',
    category: 'Moderation',
    summary: 'Kick, ban, timeout, warn, clear, softban, and nickname commands with a restart-safe temp-ban expiry loop.',
  },
  {
    id: 2,
    name: 'Moderation History & Stats',
    category: 'Moderation',
    summary: 'Per-member moderation history and server-wide moderation statistics.',
  },
  {
    id: 3,
    name: 'Security AI',
    category: 'Security',
    summary: 'Per-user threat/heat scoring (0–10) driven by scam-link, mass-mention, and bad-word pattern detection.',
  },
  {
    id: 4,
    name: 'Advanced AutoMod',
    category: 'AutoMod',
    summary: '12 independently toggleable protections — spam, caps, mentions, duplicates, invites, scam, toxicity, ghost-pings, raid detection, media, link-spam, and emoji-spam — each with its own threshold and punishment.',
    limitation: 'Member verification (CAPTCHA) has no working setup command in v18 — the button and table exist, but nothing configures them yet.',
  },
  {
    id: 5,
    name: 'Anti-Nuke',
    category: 'Security',
    summary: 'Detects and rate-limits mass-destructive actions (channel deletes, role deletes, webhook creation) with automatic punishment and a per-guild exemption whitelist.',
  },
  {
    id: 6,
    name: 'Tickets',
    category: 'Tickets',
    summary: 'Full support-ticket system with persistent open/claim/close buttons, per-guild configuration, blacklisting, and transcript generation.',
  },
  {
    id: 7,
    name: 'Welcome / Leave',
    category: 'Community',
    summary: 'Configurable join/leave messages with a placeholder engine and optional embeds.',
  },
  {
    id: 8,
    name: 'Reaction Roles',
    category: 'Community',
    summary: 'The most elaborate system in the bot: legacy reaction roles plus a full panel builder with prerequisite roles, conflicts, verification-gating, temporary roles, pricing, minimum level, bundles, and live analytics.',
    limitation: 'The "price" role condition is fully implemented, but v18 has no way for members to ever earn currency — balances stay at zero.',
  },
  {
    id: 9,
    name: 'Leveling / XP',
    category: 'Community',
    summary: 'Message and voice XP, prestige progression, role rewards with stacking, XP boosts, and all-time/weekly leaderboards.',
  },
  {
    id: 10,
    name: 'Booster Roles',
    category: 'Community',
    summary: 'Lets server boosters create and edit their own custom-colored, custom-icon role.',
  },
  {
    id: 11,
    name: 'Giveaways',
    category: 'Community',
    summary: 'Reaction-based giveaways with role requirements, blacklists, and automatic winner-rolling.',
  },
  {
    id: 12,
    name: 'AFK',
    category: 'Utility',
    summary: 'Set an AFK status that auto-clears and notifies anyone who mentions you.',
  },
  {
    id: 13,
    name: 'Reminders',
    category: 'Automation',
    summary: 'Personal one-off and recurring reminders with combined-duration parsing, delivered by channel ping or DM.',
  },
  {
    id: 14,
    name: 'Announcements',
    category: 'Owner',
    summary: 'A global auto-join welcome message plus per-server opt-in/opt-out for bot-owner broadcasts.',
  },
  {
    id: 15,
    name: 'AutoResponder',
    category: 'Automation',
    summary: 'Custom trigger-and-response automation with embeds, link buttons, response variants, cooldowns, and conditions.',
  },
  {
    id: 16,
    name: 'Emoji & Sticker Stealing',
    category: 'Utility',
    summary: 'Copy custom emojis and stickers from other servers via paste, link, reply, or right-click.',
  },
  {
    id: 17,
    name: 'Translation',
    category: 'Settings',
    summary: 'Message translation with automatic source-language detection and a right-click shortcut.',
  },
  {
    id: 18,
    name: 'Backup / Restore',
    category: 'Backups',
    summary: 'Configuration-only or full structural server backups, stored per-guild and downloadable as JSON.',
  },
  {
    id: 19,
    name: 'Starboard',
    category: 'Community',
    summary: 'Reposts popular messages into a dedicated channel once they collect enough of a configured reaction.',
  },
  {
    id: 20,
    name: 'Suggestions',
    category: 'Community',
    summary: 'Modal-based suggestion submission with persistent vote buttons, plus a staff quick-action control panel.',
  },
  {
    id: 21,
    name: 'Logging',
    category: 'Logging',
    summary: 'Centralized logging pipeline covering 12 toggleable event types, with auto-healing channel setup.',
    limitation: 'Message edit/delete, member/role update, and voice logs are always-on and cannot be individually disabled.',
  },
  {
    id: 22,
    name: 'Analytics',
    category: 'Analytics',
    summary: 'Server analytics covering members, activity, channels, voice, moderation, and engagement — never stores message content.',
  },
  {
    id: 23,
    name: 'Utility',
    category: 'Utility',
    summary: 'Server info, user info, bot info, ping, and avatar lookup.',
  },
  {
    id: 24,
    name: 'Prefix',
    category: 'Settings',
    summary: 'Per-guild custom command prefix for text commands.',
  },
  {
    id: 25,
    name: 'Help',
    category: 'Help',
    summary: 'Fully data-driven interactive command reference, generated live from the actual registered command tree.',
  },
  {
    id: 26,
    name: 'Dem AI',
    category: 'Dem AI',
    summary: 'A multi-provider AI assistant platform with automatic failover across 8 text providers and 4 image providers, conversation memory, response caching, and health monitoring.',
  },
];
